// ---------------------------------------------------------------------------
// useSavePersona Hook
// ---------------------------------------------------------------------------
// Handles saving funnel configuration as a named persona.
// Uses the unified personaService (discovery_personas table).
// ---------------------------------------------------------------------------

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { personaService, type Persona } from '@/services/v11/personaService'
import type { DimensionType, UnitType } from '@/components/v11/funnel/types'

// Keep the old input type for backward compatibility with Funnel callers
export interface SavePersonaInput {
  readonly name: string
  readonly description?: string
  readonly layers: readonly {
    readonly type: DimensionType
    readonly dimension: string
    readonly values: readonly (string | number)[]
    readonly dimensionLabel: string
    readonly valueLabels: readonly string[]
  }[]
  readonly finalCount: number
  readonly finalUnit: UnitType
  readonly totalOrigin: number
}

export function useSavePersona() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (input: SavePersonaInput) =>
      personaService.create({
        name: input.name,
        description: input.description,
        persona_type: 'funnel',
        layers: input.layers,
        datasource: 'profiles',
        finalCount: input.finalCount,
        finalUnit: input.finalUnit,
        totalOrigin: input.totalOrigin,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personas'] })
    },
  })

  return {
    savePersona: mutation.mutateAsync,
    isSaving: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
    savedPersona: mutation.data as Persona | undefined,
  }
}
